/* Public site origin — canonical URLs and Open Graph always point at the public domain, not the API host */
const SITE_URL = "https://bookify.kz"

/* Site-wide constants reused across every page */
const SITE_NAME = "Bookify"
const DEFAULT_LOCALE = "ru_KZ"
const DEFAULT_IMAGE = `${SITE_URL}/images/banner.png`
const DEFAULT_TITLE = "Bookify — онлайн-запись в салоны и сервисы Казахстана"
const DEFAULT_DESCRIPTION = "Bookify — онлайн-запись в салоны красоты, барбершопы, студии маникюра, автомойки и шиномонтажи Казахстана. Выбирайте мастера, время и записывайтесь за 30 секунд."

/* Weekday keys used by the backend schedule mapped to schema.org day names */
const SCHEMA_DAYS = {
    monday: "Monday",
    tuesday: "Tuesday",
    wednesday: "Wednesday",
    thursday: "Thursday",
    friday: "Friday",
    saturday: "Saturday",
    sunday: "Sunday"
}

/* Create or update a <meta> tag, keyed by name (default) or by property (Open Graph) */
const setMeta = (name, content, prop = false) => {
    const selector = prop ? `meta[property='${name}']` : `meta[name='${name}']`
    let element = document.querySelector(selector)
    if (!element) {
        element = document.createElement("meta")
        document.head.appendChild(element)
    }
    if (prop) {
        element.setAttribute("property", name)
    }
    else {
        element.name = name
    }
    element.content = content
}

/* Create or update a <link rel> tag (canonical, alternate) */
const setLink = (rel, href, hreflang) => {
    const selector = hreflang ? `link[rel='${rel}'][hreflang='${hreflang}']` : `link[rel='${rel}']`
    let element = document.querySelector(selector)
    if (!element) {
        element = document.createElement("link")
        document.head.appendChild(element)
    }
    element.rel = rel
    element.href = href
    if (hreflang) {
        element.hreflang = hreflang
    }
}

/* Create or update a JSON-LD <script> block by id */
const setJsonLd = (id, data) => {
    let element = document.getElementById(id)
    if (!element) {
        element = document.createElement("script")
        element.id = id
        element.type = "application/ld+json"
        document.head.appendChild(element)
    }
    element.textContent = JSON.stringify(data)
}

/* Drop a JSON-LD block that no longer applies to the current route */
const removeJsonLd = (id) => {
    const element = document.getElementById(id)
    if (element) {
        element.remove()
    }
}

/* Build a BreadcrumbList from a list of { name, url } steps */
export const buildBreadcrumb = (items) => {
    return {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: items.map((item, index) => ({
            "@type": "ListItem",
            position: index + 1,
            name: item.name,
            item: item.url
        }))
    }
}

/* Pick the most specific schema.org business type for a category name */
const schemaType = (category) => {
    const name = category ? `${category.name}`.toLowerCase() : ""
    if (name.includes("маник") || name.includes("ногт") || name.includes("педикюр")) {
        return "NailSalon"
    }
    if (name.includes("парикмах") || name.includes("барбер") || name.includes("волос")) {
        return "HairSalon"
    }
    if (name.includes("красот") || name.includes("космет") || name.includes("бров") || name.includes("ресниц") || name.includes("макияж")) {
        return "BeautySalon"
    }
    if (name.includes("мойк") || name.includes("шиномонт") || name.includes("авто") || name.includes("сто")) {
        return "AutomotiveBusiness"
    }
    return "LocalBusiness"
}

/* Build a LocalBusiness JSON-LD document from a loaded organization and its absolute image/url */
export const buildOrganizationLd = (organization, pageUrl, image) => {
    const data = {
        "@context": "https://schema.org",
        "@type": schemaType(organization.category),
        "@id": pageUrl,
        name: organization.name,
        url: pageUrl,
        image: image || DEFAULT_IMAGE
    }

    if (organization.about) {
        data.description = organization.about
    }
    if (organization.phone) {
        data.telephone = organization.phone
    }

    /* Postal address — locality falls back to Kazakhstan when the city is unknown */
    data.address = {
        "@type": "PostalAddress",
        addressCountry: "KZ",
        addressLocality: organization.cityName || "Казахстан"
    }
    if (organization.addr) {
        data.address.streetAddress = organization.addr
    }

    if (organization.lat && organization.lng) {
        data.geo = {
            "@type": "GeoCoordinates",
            latitude: organization.lat,
            longitude: organization.lng
        }
    }

    /* Aggregate rating — only when there are real reviews, otherwise Google flags it */
    if (organization.rating && organization.reviewsCount) {
        data.aggregateRating = {
            "@type": "AggregateRating",
            ratingValue: Number(organization.rating).toFixed(1),
            reviewCount: organization.reviewsCount,
            bestRating: 5,
            worstRating: 1
        }
    }

    /* Opening hours from the weekly schedule */
    const schedule = organization.hours && Array.isArray(organization.hours.schedule) ? organization.hours.schedule : []
    const openingHours = schedule
        .filter((day) => !day.dayOff && SCHEMA_DAYS[day.day] && day.open && day.close)
        .map((day) => ({
            "@type": "OpeningHoursSpecification",
            dayOfWeek: SCHEMA_DAYS[day.day],
            opens: day.open,
            closes: day.close
        }))
    if (openingHours.length) {
        data.openingHoursSpecification = openingHours
    }

    /* Social profiles — help Google connect the business entity */
    const sameAs = [organization.instagram, organization.tiktok, organization.whatsapp, organization.telegram, organization.threads].filter(Boolean)
    if (sameAs.length) {
        data.sameAs = sameAs
    }

    return data
}

/* Apply SEO tags for a page: title, description, canonical, robots, Open Graph, Twitter and optional JSON-LD */
export const applySeo = (options = {}) => {
    const title = options.title || DEFAULT_TITLE
    const description = options.description || DEFAULT_DESCRIPTION
    const path = options.path || window.location.pathname
    const url = options.url || `${SITE_URL}${path}`
    const image = options.image || DEFAULT_IMAGE
    const robots = options.robots || "index, follow"

    document.title = title
    setLink("canonical", url)
    setLink("alternate", url, "ru")
    setLink("alternate", url, "x-default")

    setMeta("description", description)
    setMeta("robots", robots)
    if (options.keywords) {
        setMeta("keywords", options.keywords)
    }

    setMeta("og:type", options.ogType || "website", true)
    setMeta("og:locale", DEFAULT_LOCALE, true)
    setMeta("og:site_name", SITE_NAME, true)
    setMeta("og:title", title, true)
    setMeta("og:description", description, true)
    setMeta("og:url", url, true)
    setMeta("og:image", image, true)
    setMeta("og:image:width", "1200", true)
    setMeta("og:image:height", "630", true)

    setMeta("twitter:card", "summary_large_image")
    setMeta("twitter:title", title)
    setMeta("twitter:description", description)
    setMeta("twitter:image", image)

    /* Per-route JSON-LD — clear the previous route's blocks first so nothing leaks across navigations */
    removeJsonLd("ld-page")
    removeJsonLd("ld-breadcrumb")
    if (options.jsonLd) {
        setJsonLd("ld-page", options.jsonLd)
    }
    if (options.breadcrumb) {
        setJsonLd("ld-breadcrumb", buildBreadcrumb(options.breadcrumb))
    }
}

/* Public origin exported for building absolute URLs in pages */
export { SITE_URL }
